// src/UserInfoForm.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  setDoc,
  updateDoc,
  getDoc,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, storage } from './firebase';
import { sendEmailVerification } from 'firebase/auth';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';

import { userDoc } from './utils/firestoreRefs';     // 🔑 UID-based helper
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './UserInfoForm.css';

/* ------------------------------------------------------------------ */
/*  SIMPLE DROPDOWN LISTS                                              */
/* ------------------------------------------------------------------ */
const countryList = [
  'Argentina','Australia','Brazil','Canada','China','Egypt','France','Germany',
  'India','Indonesia','Italy','Japan','Mexico','Nigeria','Philippines',
  'South Africa','South Korea','Spain','United Kingdom','United States',
];
const languageList = ['English (American)', 'English (British)'];

// Currency list (matches mobile)
const currencyList = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'KRW', symbol: '₩', name: 'Korean Won' },
  { code: 'ALL', symbol: 'L', name: 'Albanian Lek' },
];

/* ================================================================== */
/*  COMPONENT                                                         */
/* ================================================================== */
function UserInfoForm() {
  const navigate  = useNavigate();
  const location  = useLocation();        // state from registration step

  /* ----------------------------- STATE ---------------------------- */
  const [formData, setFormData] = useState({
    firstName:   '',
    lastName:    '',
    nationality: '',
    language:    '',
    gender:      '',
    email:       '',
    passportId:  '',
    passportURL: '',
  });

  // Phone number in E.164 format
  const [phoneNumber, setPhoneNumber] = useState('');

  // NEW fields to match mobile
  const [dob, setDob] = useState('');
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [venmoUsername, setVenmoUsername] = useState('');
  const [preferAlternativeReimbursement, setPreferAlternativeReimbursement] = useState(false);

  const [isFirstNameLocked, setIsFirstNameLocked] = useState(false);
  const [error,            setError]            = useState('');
  const [uploadProgress,   setUploadProgress]   = useState(0);
  const [isUploading,      setIsUploading]      = useState(false);

  // Verification
  const [emailVerified, setEmailVerified] = useState(false);

  /* ------------------------------------------------------------------ */
  /*  LOAD EXISTING PROFILE                                             */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    async function loadUserDoc() {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/signin');
        return;
      }

      setEmailVerified(currentUser.emailVerified);

      const userRef = userDoc(currentUser.uid);
      const snap    = await getDoc(userRef);
      const docData = snap.exists() ? snap.data() : {};

      const routeState = location.state || {};

      // Merge precedence: routeState ▸ Firestore ▸ Auth profile
      const mergedFirst = routeState.firstName || docData.firstName || '';
      const mergedLast  = routeState.lastName  || docData.lastName  || '';
      const mergedEmail =
        routeState.email || docData.email || currentUser.email || '';
      const mergedPhone = routeState.phone || docData.phoneNumber || '';

      setFormData({
        firstName:   mergedFirst,
        lastName:    mergedLast,
        nationality: docData.nationality || '',
        language:    docData.language    || '',
        gender:      docData.gender      || '',
        email:       mergedEmail,
        passportId:  docData.passportId  || '',
        passportURL: docData.passportURL || '',
      });

      // Load phone number (E.164 format)
      if (mergedPhone) {
        setPhoneNumber(mergedPhone);
      }

      // Load new fields to match mobile
      if (docData.dob) setDob(docData.dob);
      if (docData.preferredCurrency) setPreferredCurrency(docData.preferredCurrency);
      if (docData.venmoUsername) setVenmoUsername(docData.venmoUsername);
      if (docData.preferAlternativeReimbursement !== undefined) {
        setPreferAlternativeReimbursement(docData.preferAlternativeReimbursement);
      }

      if (docData.firstName) setIsFirstNameLocked(true);
    }

    loadUserDoc();
  }, [navigate, location]);

  /* ------------------------------------------------------------------ */
  /*  HANDLERS                                                          */
  /* ------------------------------------------------------------------ */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  /* ------------------ PASSPORT UPLOAD ------------------------------- */
  const handlePassportUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError('No user is logged in. Please sign in first.');
      navigate('/signin');
      return;
    }

    const fileRef   = ref(storage, `passportImages/${currentUser.uid}/${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const prog = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        );
        setUploadProgress(prog);
      },
      (err) => {
        console.error('Upload error:', err);
        setError('Failed to upload passport image.');
        setIsUploading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(fileRef);
        setIsUploading(false);

        const userRef = userDoc(currentUser.uid);
        await setDoc(userRef, { passportURL: downloadURL }, { merge: true });
        setFormData((p) => ({ ...p, passportURL: downloadURL }));
      },
    );
  };

  /* ------------------ PASSPORT DELETE ------------------------------- */
  const handlePassportDelete = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError('No user is logged in. Please sign in first.');
      navigate('/signin');
      return;
    }

    if (!formData.passportURL) return;

    const decodedUrl = decodeURIComponent(formData.passportURL);
    const base       = 'https://firebasestorage.googleapis.com/v0/b/';
    let filePath     = decodedUrl.split('?')[0].replace(base, '');
    filePath         = filePath.replace(`${process.env.REACT_APP_STORAGE_BUCKET}/o/`, '');
    filePath         = filePath.replace(/%2F/g, '/');

    try {
      await deleteObject(ref(storage, filePath));

      const userRef = userDoc(currentUser.uid);
      await updateDoc(userRef, { passportURL: deleteField() });
      setFormData((p) => ({ ...p, passportURL: '' }));
    } catch (err) {
      console.error('Error deleting passport image:', err);
      setError('Failed to delete passport image. Try again later.');
    }
  };

  /* ------------------ EMAIL VERIFICATION --------------------------- */
  const handleSendVerificationEmail = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      await sendEmailVerification(currentUser);
      alert('Verification email sent. Please check your inbox.');
    } catch (err) {
      console.error('Error sending verification email:', err);
      alert(`Failed to send verification email: ${err.message}`);
    }
  };

  /* ------------------ SUBMIT PROFILE ------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const required = ['lastName', 'email'];
    if (!isFirstNameLocked && !formData.firstName.trim()) {
      setError('Please provide your First Name.');
      return;
    }
    for (const f of required) {
      if (!formData[f] || formData[f].trim() === '') {
        setError(`Please fill in the required field: ${f}`);
        return;
      }
    }

    try {
      const cur = auth.currentUser;
      if (!cur) {
        alert('No user is logged in. Please sign in first.');
        navigate('/signin');
        return;
      }

      const userRef = userDoc(cur.uid);

      const payload = {
        firstName: isFirstNameLocked
          ? formData.firstName
          : formData.firstName.trim(),
        lastName:    formData.lastName.trim(),
        nationality: formData.nationality,
        language:    formData.language,
        gender:      formData.gender,
        email:       formData.email,
        passportId:  formData.passportId,
        dob:         dob || deleteField(),
        preferredCurrency: preferredCurrency,
        venmoUsername: venmoUsername.trim() || deleteField(),
        preferAlternativeReimbursement: preferAlternativeReimbursement,
        updatedAt:   serverTimestamp(),
      };

      // add or remove phone field (E.164 format)
      if (phoneNumber && phoneNumber.trim()) {
        payload.phoneNumber = phoneNumber.trim();
      } else {
        payload.phoneNumber = deleteField();
      }

      await setDoc(userRef, payload, { merge: true });
      console.log('✅ User info saved.');
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      setError('Failed to save your information. Please try again.');
    }
  };

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <div className="user-info-container">
      <button className="back-button" onClick={() => navigate(-1)}>
        Back
      </button>

      <h2 className="user-info-title">Who Dis?</h2>

      <form className="user-info-form" onSubmit={handleSubmit}>
        {error && <p className="error-text">{error}</p>}

        {/* ---------------- FIRST & LAST NAMES ---------------- */}
        <div className="form-row">
          <label htmlFor="firstName">First Name</label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            disabled={isFirstNameLocked}
          />
        </div>

        <div className="form-row">
          <label htmlFor="lastName">Last Name</label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            placeholder="Last Name *"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>

        {/* ---------------- NATIONALITY, LANGUAGE, GENDER --------------- */}
        <div className="form-row">
          <label htmlFor="nationality">Nationality</label>
          <select
            id="nationality"
            name="nationality"
            value={formData.nationality}
            onChange={handleChange}
          >
            <option value="">Select your country</option>
            {countryList.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label htmlFor="language">Language</label>
          <select
            id="language"
            name="language"
            value={formData.language}
            onChange={handleChange}
          >
            <option value="">Select your language</option>
            {languageList.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label htmlFor="gender">Gender</label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
          >
            <option value="">Select your gender</option>
            <option value="Man">Man</option>
            <option value="Woman">Woman</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* ---------------- DATE OF BIRTH ---------------- */}
        <div className="form-row">
          <label htmlFor="dob">Date of Birth</label>
          <input
            id="dob"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
          />
        </div>

        {/* ---------------- PREFERRED CURRENCY ---------------- */}
        <div className="form-row">
          <label htmlFor="preferredCurrency">Preferred Currency</label>
          <select
            id="preferredCurrency"
            value={preferredCurrency}
            onChange={(e) => setPreferredCurrency(e.target.value)}
          >
            {currencyList.map((currency) => (
              <option key={currency.code} value={currency.code}>
                {currency.symbol} {currency.code} - {currency.name}
              </option>
            ))}
          </select>
        </div>

        {/* ---------------- PHONE ---------------- */}
        <div className="form-row">
          <label htmlFor="phone">Phone Number</label>
          <PhoneInput
            international
            defaultCountry="US"
            placeholder="Enter phone number"
            value={phoneNumber}
            onChange={setPhoneNumber}
          />
        </div>

        {/* ---------------- EMAIL ---------------- */}
        <div className="form-row">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="Email Address *"
            value={formData.email}
            disabled
            required
          />
          {!emailVerified && (
            <button
              type="button"
              onClick={handleSendVerificationEmail}
              className="verify-button"
            >
              Verify Email
            </button>
          )}
        </div>

        {/* ---------------- VENMO USERNAME ---------------- */}
        <div className="form-row">
          <label htmlFor="venmoUsername">Venmo Username</label>
          <input
            id="venmoUsername"
            type="text"
            placeholder="@username (optional)"
            value={venmoUsername}
            onChange={(e) => setVenmoUsername(e.target.value)}
          />
          <p style={{ fontSize:'0.85rem', color:'#999', marginTop:'5px' }}>
            For reimbursements and trip payments
          </p>
        </div>

        {/* ---------------- ALTERNATIVE REIMBURSEMENT ---------------- */}
        <div className="form-row">
          <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }}>
            <input
              type="checkbox"
              checked={preferAlternativeReimbursement}
              onChange={(e) => setPreferAlternativeReimbursement(e.target.checked)}
            />
            <span>I prefer alternative reimbursement methods to Venmo</span>
          </label>
        </div>

        {/* ---------------- PASSPORT NUMBER ---------------- */}
        <div className="form-row">
          <label htmlFor="passportId">Passport Number</label>
          <input
            id="passportId"
            name="passportId"
            type="text"
            placeholder="Passport Number"
            value={formData.passportId}
            onChange={handleChange}
          />
        </div>

        {/* ---------------- PASSPORT IMAGE ---------------- */}
        <div className="form-row">
          <label>Passport Picture</label>
          {formData.passportURL ? (
            <img
              src={formData.passportURL}
              alt="Passport"
              style={{ width:150, border:'2px solid #555' }}
            />
          ) : (
            <p style={{ fontStyle:'italic' }}>No passport image uploaded.</p>
          )}

          {isUploading && (
            <p style={{ color:'#FFD700' }}>Uploading: {uploadProgress}%</p>
          )}

          <div className="file-input-row">
            <label className="browse-button">
              Browse
              <input
                type="file"
                accept="image/*"
                onChange={handlePassportUpload}
                style={{ display:'none' }}
              />
            </label>

            {formData.passportURL && (
              <button
                type="button"
                onClick={handlePassportDelete}
                className="delete-button"
              >
                Delete Passport Image
              </button>
            )}
          </div>

          <p className="passport-hint">
            (Optional, but many hostels require it. Show name, picture, and
            passport number. We don't check it; it's for them.)
          </p>
        </div>

        {/* ---------------- SAVE BUTTON ---------------- */}
        <div className="button-row">
          <button type="submit" className="save-button">
            Save &amp; Continue
          </button>
        </div>
      </form>
    </div>
  );
}

export default UserInfoForm;
